import { Button, ButtonTheme, BUTTON_HEIGHT } from 'components/Button';
import { Icon, ICONS } from 'components/Icon';
import { Menu } from 'components/Menu';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { pureComponent } from 'lib/pureComponent';
import { randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { Platform, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { useDispatch } from 'react-redux';
import { FileTypeGroupsEnum } from 'service/file/types';
import {
  doAseOpenAssetFiles,
  doAssetOpenFiles,
  doAssetFileGetPhotoFromPhoto,
  eventTypeFilesSelected,
  doAppFileGetFilesFromPicker,
  doAppFileGetImagesFromPicker,
} from 'store/actions';
import { FilesContext, FilesContextEnum } from 'types/types';

interface Props {
  context: FilesContext;
  promptMessage?: string;
  label?: string;
  enableFileManager?: boolean;
  multiSelect?: boolean;
  onChange?: (fileIds: string[]) => void;
  style?: StyleProp<ViewStyle>;
  showAddLabel?: boolean;
  onRemove?: () => void;
  shadow?: boolean;
  type?: 'images' | 'files';
  maximized?: boolean;
  removeText?: string;
  disabled?: boolean;
}

export const FileAddButtons: React.FC<Props> = pureComponent(
  ({
    context,
    enableFileManager = true,
    multiSelect = false,
    onChange,
    onRemove,
    promptMessage,
    shadow,
    showAddLabel = true,
    style,
    type = 'files',
    maximized,
    removeText,
    disabled,
  }) => {
    const dispatch = useDispatch();
    const eventKey = React.useRef(randomKey());

    const handleAddFromFileManager = () =>
      context.context === FilesContextEnum.localAssessment ||
      context.context === FilesContextEnum.remoteAssessment
        ? dispatch(
            doAseOpenAssetFiles({
              limitSelectionToFileGroups:
                type === 'images' ? [FileTypeGroupsEnum.image] : undefined,
              multiSelect,
              promptMessage,
              eventKey: eventKey.current,
            }),
          )
        : dispatch(
            doAssetOpenFiles({
              limitSelectionToFileGroups:
                type === 'images' ? [FileTypeGroupsEnum.image] : undefined,
              assetId: context.assetId,
              multiSelect,
              promptMessage,
              eventKey: eventKey.current,
            }),
          );

    React.useEffect(() => {
      const events = [
        doAddEvent(eventTypeFilesSelected, (event) => {
          if (
            event.payload.eventKey === eventKey.current &&
            event.payload.fileIds.length
          ) {
            onChange?.(event.payload.fileIds);
          }
        }),
      ];
      return () => {
        doRemoveEvents(events);
      };
    }, [onChange]);

    const buttonChildren = [];
    const otherButtonsConfig: {
      key: string;
      text: string;
      icon: ICONS;
      onPress: () => void;
      disabled?: boolean;
      destructive?: boolean;
    }[] = [];

    if (
      context.context === FilesContextEnum.localAsset ||
      context.context === FilesContextEnum.localAssessment
    ) {
      buttonChildren.push(
        <Button
          key="camera"
          buttonTheme={ButtonTheme.themeless}
          icon={ICONS.CameraLight}
          onPress={() =>
            dispatch(
              doAssetFileGetPhotoFromPhoto({
                assetId: context.assetId,
                eventKey: eventKey.current,
              }),
            )
          }
          style={styles.button}
          disabled={disabled}
        />,
      );
      if (Platform.OS === 'web') {
        if (type === 'images') {
          buttonChildren.push(
            <Button
              key="image"
              buttonTheme={ButtonTheme.themeless}
              icon={ICONS.ImageLight}
              onPress={() =>
                dispatch(
                  doAppFileGetImagesFromPicker({
                    assetId: context.assetId,
                    eventKey: eventKey.current,
                    multiSelect,
                    returnType: 'saveToAsset',
                  }),
                )
              }
              style={styles.button}
              disabled={disabled}
            />,
          );
        } else {
          buttonChildren.push(
            <Button
              key="file"
              buttonTheme={ButtonTheme.themeless}
              icon={ICONS.FolderOpenLight}
              onPress={() =>
                dispatch(
                  doAppFileGetFilesFromPicker({
                    eventKey: eventKey.current,
                    returnType: 'saveToAsset',
                    assetId: context.assetId,
                    multiSelect,
                    allowedFileGroups: null,
                  }),
                )
              }
              style={styles.button}
              disabled={disabled}
            />,
          );
        }
      } else {
        buttonChildren.push(
          <Button
            key="image"
            buttonTheme={ButtonTheme.themeless}
            icon={ICONS.ImageLight}
            onPress={() =>
              dispatch(
                doAppFileGetImagesFromPicker({
                  assetId: context.assetId,
                  eventKey: eventKey.current,
                  multiSelect,
                  returnType: 'saveToAsset',
                }),
              )
            }
            style={styles.button}
            disabled={disabled}
          />,
        );
        if (type === 'files') {
          otherButtonsConfig.push({
            key: 'devicefiles',
            text: 'Choose from Device Files',
            icon: ICONS.FolderOpenLight,
            disabled,
            onPress: () =>
              dispatch(
                doAppFileGetFilesFromPicker({
                  eventKey: eventKey.current,
                  returnType: 'saveToAsset',
                  assetId: context.assetId,
                  multiSelect,
                  allowedFileGroups: null,
                }),
              ),
          });
        }
      }
    }

    if (enableFileManager) {
      otherButtonsConfig.push({
        key: 'assetfiles',
        text: 'Choose from Asset',
        icon: ICONS.RBPlectrumLight,
        onPress: handleAddFromFileManager,
        disabled,
      });
    }

    if (onRemove) {
      otherButtonsConfig.push({
        key: 'remove',
        text: removeText || 'Remove',
        icon: ICONS.TrashCanLight,
        destructive: true,
        onPress: onRemove,
        disabled,
      });
    }

    if (otherButtonsConfig.length) {
      if (maximized) {
        buttonChildren.push(
          ...otherButtonsConfig.map((conf) => (
            <Button
              key={conf.key}
              onPress={conf.onPress}
              icon={conf.icon}
              buttonTheme={ButtonTheme.themeless}
              style={styles.button}
              disabled={conf.disabled}
              destructive={conf.destructive}
            />
          )),
        );
      } else {
        buttonChildren.push(
          <Menu
            key="other"
            buttonConfig={{
              buttonTheme: ButtonTheme.themeless,
              style: styles.button,
              icon: ICONS.EllipsisLight,
            }}
            width={200}
            options={otherButtonsConfig}
            disabled={disabled}
          />,
        );
      }
    }

    if (!buttonChildren.length) {
      return null;
    }

    return (
      <View
        style={[
          styles.buttons,
          shadow ? styles.buttonsShadow : undefined,
          style,
        ]}
      >
        {showAddLabel && (
          <View style={styles.label}>
            <Icon size={12} color="white" source={ICONS.PlusLight} />
          </View>
        )}
        <ViewWithSeparators
          horizontal
          separator={<View style={styles.divider} />}
        >
          {buttonChildren}
        </ViewWithSeparators>
      </View>
    );
  },
);

const styles = StyleSheet.create({
  button: {
    borderRadius: 0,
    height: BUTTON_HEIGHT - 2,
    paddingLeft: theme.paddingHorizontal - 2,
    paddingRight: theme.paddingHorizontal - 2,
  },
  buttons: {
    backgroundColor: 'white',
    borderColor: theme.colors.blue80,
    borderRadius: 4,
    borderWidth: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    height: BUTTON_HEIGHT,
  },
  buttonsShadow: {
    shadowColor: theme.colors.blue,
    shadowOffset: { height: 0, width: 0 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  container: {
    backgroundColor: 'white',
    paddingHorizontal: theme.paddingHorizontal,
    paddingVertical: theme.grid,
  },
  divider: {
    borderRightColor: theme.colors.blue80,
    borderRightWidth: 1,
    height: BUTTON_HEIGHT - 2,
  },
  imageGridContainer: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingBottom: theme.grid,
  },
  label: {
    alignItems: 'center',
    backgroundColor: theme.colors.blue80,
    justifyContent: 'center',
    minWidth: theme.grid * 2,
  },
});
